import { createFeatureFlags } from '@sitecore/featureflags';

export const defaultFeatureFlags = {
  MFA: true,
  SNOW: true,
  APP_SWITCHER: true,
  EXPORT_MEMBERS: true,
  INVITATIONS: true,
  SSO: true,
  NEW_FEATURE_ANNOUNCEMENT: false,
  SUBSCRIPTIONS: false,
  SAML: false,
  OPENID: false,
  PROVISION_APPS: true,
  PROVISION_STREAM: false,
  RECOMMENDATIONS: true,
  SUPPORT_ACCESS: false,
  CONTEXT_MAPPING: true,
  CUSTOM_CONTEXT_MAPPING: true,
  SCOPED_CONTEXT_MAPPING: true,
  CUSTOM_HOSTNAMES: true,
  CLAIMS_MAPPING: false,
  UNIFIED_TRACKING: false,
  KC_BOT: false,
  AUDIT_LOGS: true,
  CONTENT_HUB_TOGGLE: true,
  USAGE_TRACKING: false,
  USER_GROUPS: false,
  SYNC_WITH_MASTER_CONTEXT: true,
  GO_LIVE_PLANNING: true,
  SCAI_DAM: false,
};

export const {
  Feature,
  FlagsProvider,
  useFeature,
  useFeatures,
  withFeature,
  behindFeature,
} = createFeatureFlags(defaultFeatureFlags);

export type FeatureFlags = ReturnType<typeof useFeatures>;
export type FeatureName = keyof FeatureFlags;

interface Organization {
  [id: string]: string | string[];
}

export interface EnvironmentFlags {
  MFA: Organization;
  SNOW: Organization;
  APP_SWITCHER: Organization;
  EXPORT_MEMBERS: Organization;
  INVITATIONS: Organization;
  SSO: Organization;
  NEW_FEATURE_ANNOUNCEMENT: Organization;
  SUBSCRIPTIONS: Organization;
  SAML: Organization;
  OPENID: Organization;
  PROVISION_APPS: Organization;
  PROVISION_STREAM: Organization;
  RECOMMENDATIONS: Organization;
  SUPPORT_ACCESS: Organization;
  CONTEXT_MAPPING: Organization;
  CUSTOM_CONTEXT_MAPPING: Organization;
  SCOPED_CONTEXT_MAPPING: Organization;
  CUSTOM_HOSTNAMES: Organization;
  CLAIMS_MAPPING: Organization;
  UNIFIED_TRACKING: Organization;
  KC_BOT: Organization;
  AUDIT_LOGS: Organization;
  CONTENT_HUB_TOGGLE: Organization;
  USAGE_TRACKING: Organization;
  USER_GROUPS: Organization;
  SYNC_WITH_MASTER_CONTEXT: Organization;
  GO_LIVE_PLANNING: Organization;
  SCAI_DAM: Organization;
}

export interface Environments {
  dev: EnvironmentFlags;
  qa: EnvironmentFlags;
  staging: EnvironmentFlags;
  preprod: EnvironmentFlags;
  prod: EnvironmentFlags;
}

//todo flag ids not to disclosure the feature name
export const organizationConditionsPerEnvironment: Environments = {
  dev: {
    MFA: {},
    SNOW: {},
    APP_SWITCHER: {},
    EXPORT_MEMBERS: {},
    INVITATIONS: {},
    SSO: {},
    NEW_FEATURE_ANNOUNCEMENT: {},
    SUBSCRIPTIONS: {},
    SAML: {},
    OPENID: {},
    PROVISION_APPS: {},
    PROVISION_STREAM: {},
    RECOMMENDATIONS: {},
    SUPPORT_ACCESS: {},
    CONTEXT_MAPPING: {},
    CUSTOM_CONTEXT_MAPPING: {},
    SCOPED_CONTEXT_MAPPING: {},
    CUSTOM_HOSTNAMES: {},
    CLAIMS_MAPPING: {},
    UNIFIED_TRACKING: {},
    KC_BOT: {},
    AUDIT_LOGS: {},
    CONTENT_HUB_TOGGLE: {},
    USAGE_TRACKING: {},
    USER_GROUPS: {},
    SYNC_WITH_MASTER_CONTEXT: {},
    GO_LIVE_PLANNING: {},
    SCAI_DAM: {
      org_MDWkPgSdDkzCrPxN: 'E2E Test Org For CH Delivery',
    },
  },
  qa: {
    MFA: {},
    SNOW: {},
    APP_SWITCHER: {},
    EXPORT_MEMBERS: {},
    INVITATIONS: {},
    SSO: {},
    NEW_FEATURE_ANNOUNCEMENT: {},
    SUBSCRIPTIONS: {},
    SAML: {},
    OPENID: {},
    PROVISION_APPS: {},
    PROVISION_STREAM: {},
    RECOMMENDATIONS: {},
    SUPPORT_ACCESS: {},
    CONTEXT_MAPPING: {},
    CUSTOM_CONTEXT_MAPPING: {},
    SCOPED_CONTEXT_MAPPING: {},
    CUSTOM_HOSTNAMES: {},
    CLAIMS_MAPPING: {},
    UNIFIED_TRACKING: {},
    KC_BOT: {},
    AUDIT_LOGS: {},
    CONTENT_HUB_TOGGLE: {},
    USAGE_TRACKING: {},
    USER_GROUPS: {},
    SYNC_WITH_MASTER_CONTEXT: {},
    GO_LIVE_PLANNING: {},
    SCAI_DAM: {},
  },
  staging: {
    MFA: {},
    SNOW: {},
    APP_SWITCHER: {},
    EXPORT_MEMBERS: {},
    INVITATIONS: {},
    SSO: {},
    NEW_FEATURE_ANNOUNCEMENT: {
      org_NngeQKXoFUObloUL: 'customer-portal',
      orgTypeFilter: ['unspecified', 'internal', 'partner', 'customer'],
    },
    SUBSCRIPTIONS: {
      org_Lztbh9wBHCl2oyBL: 'test-app-organization-0lq01b',
      org_gM6bfxVu8y5MHfEp: 'ops-demo-org',
    },
    SAML: {},
    OPENID: {},
    PROVISION_APPS: {},
    PROVISION_STREAM: {
      org_Lztbh9wBHCl2oyBL: 'This is the test app org',
      org_FijrH7yaknrqmRcI: 'Portal Org',
    },
    RECOMMENDATIONS: {},
    SUPPORT_ACCESS: {},
    CONTEXT_MAPPING: {},
    CUSTOM_CONTEXT_MAPPING: {},
    SCOPED_CONTEXT_MAPPING: {},
    CUSTOM_HOSTNAMES: {},
    CLAIMS_MAPPING: {},
    UNIFIED_TRACKING: {
      org_8UX5nhL9vs961j2R: 'CDPP Development Org',
      org_IKF49tqy6kcfWN2Z: 'DevEx Organization',
    },
    KC_BOT: {},
    AUDIT_LOGS: {},
    CONTENT_HUB_TOGGLE: {},
    USAGE_TRACKING: {},
    USER_GROUPS: { orgTypeFilter: ['internal'] },
    SYNC_WITH_MASTER_CONTEXT: {},
    GO_LIVE_PLANNING: {},
    SCAI_DAM: {},
  },
  preprod: {
    MFA: {},
    SNOW: {},
    APP_SWITCHER: {},
    EXPORT_MEMBERS: {},
    INVITATIONS: {},
    SSO: {},
    NEW_FEATURE_ANNOUNCEMENT: {
      orgTypeFilter: ['unspecified', 'internal', 'partner', 'customer'],
    },
    SUBSCRIPTIONS: {},
    SAML: {},
    OPENID: {},
    PROVISION_APPS: {},
    PROVISION_STREAM: {},
    RECOMMENDATIONS: {},
    SUPPORT_ACCESS: {},
    CONTEXT_MAPPING: {},
    CUSTOM_CONTEXT_MAPPING: {},
    SCOPED_CONTEXT_MAPPING: {},
    CUSTOM_HOSTNAMES: {},
    CLAIMS_MAPPING: {},
    UNIFIED_TRACKING: {},
    KC_BOT: {},
    AUDIT_LOGS: {},
    CONTENT_HUB_TOGGLE: {},
    USAGE_TRACKING: { orgTypeFilter: ['internal'] },
    USER_GROUPS: { orgTypeFilter: ['internal'] },
    SYNC_WITH_MASTER_CONTEXT: {},
    GO_LIVE_PLANNING: {},
    SCAI_DAM: {},
  },
  prod: {
    MFA: {},
    SNOW: {},
    APP_SWITCHER: {},
    EXPORT_MEMBERS: {},
    INVITATIONS: {},
    SSO: {},
    NEW_FEATURE_ANNOUNCEMENT: {
      orgTypeFilter: ['unspecified', 'internal', 'partner', 'customer'],
    },
    SUBSCRIPTIONS: {
      org_E2A54NDGy5IMoO5X: 'Emirates',
      org_fcZMCki4V9rCDzjB: 'Xmcloud Customer Test',
    },
    SAML: {},
    OPENID: {},
    PROVISION_APPS: {},
    PROVISION_STREAM: { orgTypeFilter: ['unspecified', 'partner', 'customer'] },
    RECOMMENDATIONS: {},
    SUPPORT_ACCESS: {},
    CONTEXT_MAPPING: {},
    CUSTOM_CONTEXT_MAPPING: {},
    SCOPED_CONTEXT_MAPPING: {},
    CUSTOM_HOSTNAMES: {},
    CLAIMS_MAPPING: {},
    UNIFIED_TRACKING: { org_7qihOdknvMrfI0hj: 'Personalize Team' },
    KC_BOT: {},
    AUDIT_LOGS: {},
    CONTENT_HUB_TOGGLE: {},
    USAGE_TRACKING: { orgTypeFilter: ['internal'] },
    USER_GROUPS: { orgTypeFilter: ['internal'] },
    SYNC_WITH_MASTER_CONTEXT: {},
    GO_LIVE_PLANNING: {},
    SCAI_DAM: {
      org_625ibYKGjiRYJB67: 'UniP Test Aue 28',
      org_IUyhKWqudrYvzlFA: 'sops-scai-dam-aue-01',
      org_heSJ8P5PqGl6SAxq: 'sops-scai-dam-use-02',
      org_mqTuSCoflBtCXr3W: 'sops-scai-dam-usw-01',
      org_GTwDaugaqtc7ULrH: 'sops-scai-dam-sg-02',
      org_GsPgbAzOanynCX1o: 'sops-scai-dam-jpe-02',
      org_uTqpP0vPGZgXiilB: 'sops-scai-dam-ae-01',
      org_gyELHkgyHnyylmt: 'sops-scai-up-01',
      org_KV9oZUkDFI922y9D: 'sops-scai-up-02',
      org_JPv4G8ikNAAQhJec: 'sops-scai-up-03',
      org_cACbbMd09ZChQ7g7: 'sops-scai-up-04',
      org_DsPK1TVLe2sFTnpr: 'sops-scai-up-05',
      org_TNPIPqU6pxiSDYIW: 'sops-scai-up-06',
      org_Y19UgUEQeoz21XzF: 'sops-scai-up-07',
    },
  },
};
